import { showHttpError } from '@calendly/alert'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { setFormErrors } from 'actions/form'
import changeSet from 'core/change_set'

import {
  createTeam,
  fetchTeam,
  getOrganizationInvitations,
  getOrganizationMemberships,
  getOrganizationMigrations,
  requestSlugGeneration,
  updateTeam
} from './api'

const isAdmin = (role) => ['admin', 'owner'].includes(role)

const membershipToSuggestion = (membership) => ({
  id: null,
  email: membership.email,
  owner_id: membership.id,
  owner_type: 'OrganizationMembership',
  edit_permission: isAdmin(membership.role),
  role_edit_permission: isAdmin(membership.role),
  name: membership.name,
  avatar_letter: membership.avatar_letter,
  avatar_url: membership.avatar_url
})

export const membershipToInitialSuggestion = (membership) => ({
  ...membershipToSuggestion(membership),
  edit_permission: true
})

const invitationToSuggestion = (invitation) => ({
  id: null,
  email: invitation.email,
  owner_id: invitation.id,
  owner_type: 'OrganizationInvitation',
  edit_permission: isAdmin(invitation.role),
  role_edit_permission: isAdmin(invitation.role),
  avatar_letter: invitation.avatar_letter,
  avatar_url: null
})

const migrationToSuggestion = (migration) => ({
  id: null,
  email: migration.invitation.email,
  owner_id: migration.invitation.id,
  owner_type: 'OrganizationInvitation',
  edit_permission: isAdmin(migration.invitation.role),
  role_edit_permission: isAdmin(migration.invitation.role),
  name: migration.invitation.name,
  avatar_letter: migration.invitation.avatar_letter,
  avatar_url: migration.invitation.avatar_url,
  migrating: true
})

export const fetchSuggestions = createAsyncThunk(
  'teamForm/suggestions/fetch',
  async (query, { dispatch, rejectWithValue }) => {
    if (!query) {
      return []
    }

    const gettingMemberships = getOrganizationMemberships(query)
    const gettingInvitations = getOrganizationInvitations(query)
    const gettingMigrations = getOrganizationMigrations(query)

    const [
      [successMemberships, responseMemberships],
      [successInvitations, responseInvitations],
      [successMigrations, responseMigrations]
    ] = await Promise.all([
      gettingMemberships,
      gettingInvitations,
      gettingMigrations
    ])

    if (!successInvitations || !successMemberships || !successMigrations) {
      dispatch(showHttpError(
        (!successMemberships && responseMemberships)
        || (!successInvitations && responseInvitations)
        || (!successMigrations && responseMigrations)
      ))
      return rejectWithValue([])
    }

    return [
      ...responseMemberships.results.map(membershipToSuggestion),
      ...responseInvitations.results.map(invitationToSuggestion),
      ...responseMigrations.results.map(migrationToSuggestion)
    ]
  }
)

export const loadTeam = createAsyncThunk(
  'teamForm/fetch',
  async (teamId, { rejectWithValue }) => {
    const [success, response] = await fetchTeam({ id: teamId })

    if (!success) {
      return rejectWithValue(null)
    }

    return response
  }
)

export const generateSlug = createAsyncThunk(
  'teamForm/generateSlug',
  async (teamName, { rejectWithValue }) => {
    const [success, response] = await requestSlugGeneration({ name: teamName })

    if (!success) {
      return rejectWithValue(null)
    }

    return response.slug
  }
)

const eventTypesTeamPath = (id) => `/event_types/team/${id}`

const teamErrorKeyMap = {
  'profile.name': 'name',
  'profile.slug': 'slug',
  'profile.description': 'description',
  'profile.avatar': 'avatar'
}

const membersListToAttributes = (members) => (
  JSON.stringify(members.filter(({ id, _destroy }) => !_destroy || id !== null))
)

const appendAvatarAttributes = (formData, { file, crop, remove }) => {
  if (file) {
    formData.append('page[profile_attributes][avatar]', file)
    if (crop) {
      formData.append('page[profile_attributes][avatar_crop]', JSON.stringify(crop))
    }
    return
  }

  if (remove) {
    formData.append('page[profile_attributes][remove_avatar]', '1')
  }
}

export const submitTeamCreation = (data) => async (dispatch) => {
  const formData = new FormData()
  const { members, name, slug, timezone, description, avatar } = data
  formData.append('page[profile_attributes][name]', name)
  formData.append('page[profile_attributes][slug]', slug)
  formData.append('page[profile_attributes][timezone]', timezone)
  formData.append('page[profile_attributes][description]', description)
  formData.append('page[memberships_json]', membersListToAttributes(members))
  appendAvatarAttributes(formData, avatar)

  const [success, response] = await createTeam(formData)

  if (success) {
    changeSet.reset()
    window.location.assign(eventTypesTeamPath(response.id))
  } else {
    dispatch(setFormErrors(response, teamErrorKeyMap))
  }
}

const slice = createSlice({
  name: 'teamForm',
  initialState: {
    generatedSlug: null,
    isGeneratingSlug: false,
    pending: true,
    creating: false,
    suggestions: [],
    currentTeam: {
      page_id: null
    }
  },
  reducers: {
    clearGeneratedSlug: (state) => {
      state.generatedSlug = null
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadTeam.fulfilled, (state, action) => {
      state.currentTeam = action.payload
    })
    builder.addCase(loadTeam.rejected, (state) => {
      state.fatalError = true
    })
    builder.addCase(generateSlug.fulfilled, (state, action) => {
      state.generatedSlug = action.payload
      state.isGeneratingSlug = false
    })
    builder.addCase(generateSlug.pending, (state) => {
      state.generatedSlug = null
      state.isGeneratingSlug = true
    })
    builder.addCase(generateSlug.rejected, (state) => {
      state.isGeneratingSlug = false
    })
    builder.addCase(fetchSuggestions.pending, (state) => {
      state.pending = true
    })
    builder.addCase(fetchSuggestions.fulfilled, (state, action) => {
      state.pending = false
      state.suggestions = action.payload
    })
    builder.addCase(fetchSuggestions.rejected, (state, action) => {
      state.pending = false
      state.suggestions = action.payload
    })
  }
})

export const submitTeamUpdate = (id, values, returnTo) => async (dispatch) => {
  const {
    name, slug, timezone, description, new_members_enabled: newMembersEnabled,
    members, avatar
  } = values
  const formData = new FormData()
  formData.append('page[profile_attributes][name]', name)
  formData.append('page[profile_attributes][slug]', slug)
  formData.append('page[profile_attributes][timezone]', timezone)
  formData.append('page[profile_attributes][description]', description)
  formData.append('page[new_members_enabled]', newMembersEnabled)
  formData.append('page[memberships_json]', membersListToAttributes(members))
  appendAvatarAttributes(formData, avatar)

  const [success, response] = await updateTeam(id, formData)
  if (success) {
    changeSet.reset()
    window.location.assign(returnTo || eventTypesTeamPath(id))
  } else {
    dispatch(setFormErrors(response, teamErrorKeyMap))
  }
}

export const { reducer, actions: { clearGeneratedSlug } } = slice
